import { useEffect, useState } from "react";

/**
 * Tracks whether the viewport is at or below `breakpointPx`, reactively.
 *
 * Driven by `window.innerWidth` + a `resize` listener rather than
 * `matchMedia`, so it behaves predictably under jsdom in tests (jsdom does
 * not implement `matchMedia` meaningfully) as well as in real browsers.
 *
 * The breakpoint values themselves come from `styles/tokens.css`'s
 * documented reference (narrow: 768px / 48rem, medium: 1024px / 64rem) —
 * kept here as a plain number because media queries, and this hook, can't
 * read CSS custom properties.
 */
export function useIsNarrow(breakpointPx = 768): boolean {
  const [isNarrow, setIsNarrow] = useState(() => matches(breakpointPx));

  useEffect(() => {
    const onResize = () => setIsNarrow(matches(breakpointPx));
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpointPx]);

  return isNarrow;
}

function matches(breakpointPx: number): boolean {
  return typeof window !== "undefined" && window.innerWidth <= breakpointPx;
}
